/**
 * Achievements Domain
 *
 * Manages the client-side celebration queue for awarded achievements.
 * When the server sends OpCodeAchievementAwarded (340), a celebration is enqueued.
 * The AchievementCelebrationModal displays the first item in the queue; on dismiss it
 * triggers the appropriate post-achievement dialogue and removes the item from the queue.
 *
 * Decoupled from episodes — achievements can be triggered from any server-side context.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MessageType } from '../constants/op-codes';
import type { AchievementAwardedPayload } from '../constants/op-codes';
import i18n from '../i18n/config';
import { openDialogue } from './dialogue';
import { sendActivityAction, useTeamActivityStore } from './team-activity';

// ==================== TYPES ====================

interface AchievementCelebration {
  /** Stable achievement ID (e.g. "teamwork_excellence"). */
  achievementId: string;
  /** i18n key for the achievement name (e.g. "achievements.teamworkExcellence.name"). */
  nameKey: string;
  /** i18n key for the achievement description. */
  descriptionKey: string;
  /** Client-side image identifier mapped in the achievement image registry. */
  imageId: string;
  /**
   * Dialogue to play after the celebration is dismissed, as a congratulations message.
   * If set, the popup triggers this dialogue on close.
   */
  dialogueId?: string;
  /**
   * Runtime variables passed to the dialogue (e.g. achievement name for interpolation).
   */
  dialogueVariables?: Record<string, string>;
}

/** A celebration as it waits its turn, numbered by its award: two of one achievement are two moments. */
interface QueuedAchievementCelebration extends AchievementCelebration {
  award: number;
}

interface AchievementsState {
  /** Queue of pending achievement celebrations. First item is the one being shown. */
  celebrationQueue: QueuedAchievementCelebration[];

  enqueueCelebration: (celebration: AchievementCelebration) => void;
  /**
   * Dismiss the current (first) celebration and optionally trigger a dialogue.
   * Triggers the celebration's dialogueId (if any) before removing it from the queue.
   */
  dismissCelebration: () => void;
  /** No celebration waiting, as a player leaves a match. */
  reset: () => void;
}

// ==================== STORE ====================

/** The awards queued so far, which numbers the next. */
let awards = 0;

export const useAchievementsStore = create<AchievementsState>()(
  devtools(
    (set, get) => ({
      celebrationQueue: [],

      enqueueCelebration: (celebration) => {
        awards += 1;
        const award = awards;
        set(
          (state) => ({ celebrationQueue: [...state.celebrationQueue, { ...celebration, award }] }),
          false,
          'achievements/enqueueCelebration',
        );
      },

      dismissCelebration: () => {
        const current = get().celebrationQueue[0];
        set(
          (state) => ({ celebrationQueue: state.celebrationQueue.slice(1) }),
          false,
          'achievements/dismissCelebration',
        );

        // After dismissal, trigger the post-achievement dialogue (if configured).
        if (!current?.dialogueId) {
          // No dialogue — the earner is done immediately; send acknowledgement now.
          const s = useTeamActivityStore.getState();
          if (s.active && s.phaseType === 'earn_achievement') {
            void sendActivityAction({ action: 'achievement_acknowledged' });
          }
          return;
        }

        // Resolve the achievementNameKey variable to an actual translated string
        // so i18next can interpolate it directly into dialogue textParams.
        const vars = current.dialogueVariables ? { ...current.dialogueVariables } : {};
        if (vars.achievementNameKey) {
          vars.achievementName = i18n.t(vars.achievementNameKey);
          delete vars.achievementNameKey;
        }
        openDialogue(current.dialogueId, vars);
      },

      reset: () => set({ celebrationQueue: [] }, false, 'achievements/reset'),
    }),
    { name: 'AchievementsStore' },
  ),
);

// ==================== HANDLER ====================

/**
 * Handle an incoming OpCodeAchievementAwarded (340) message from the server.
 * Enqueues a celebration for the awarded achievement.
 *
 * The dialogue triggered after dismiss depends on the achievement:
 *  - "teamwork_excellence" → 01-session-script-intro-session/10-debrief-achievement (with achievement name variable)
 *  - Other achievements → no automatic dialogue (can be extended later)
 */
export function handleAchievementAwarded(data: AchievementAwardedPayload): void {
  console.log('[Achievements] Received achievement:', data.achievement_id);

  const celebration: AchievementCelebration = {
    achievementId: data.achievement_id,
    nameKey: data.name_key,
    descriptionKey: data.description_key,
    imageId: data.image_id,
  };

  if (data.achievement_id === 'teamwork_excellence') {
    celebration.dialogueId = '01-session-script-intro-session/10-debrief-achievement';
    celebration.dialogueVariables = { achievementNameKey: data.name_key };
  }

  useAchievementsStore.getState().enqueueCelebration(celebration);
}

// ==================== THE PLAYER'S ACTS ====================

/** The player closes the award's moment (its Continue): the next award waiting opens, and the line that follows one. */
export function dismissAchievementMoment(): void {
  useAchievementsStore.getState().dismissCelebration();
}

// ==================== PREVIEW ====================

/**
 * The admin tools' preview of an award: Teamwork Excellence, as the server
 * sends it, through the award's own handler — which says what follows it, the
 * advisor's line — so the window builds no message of the server's.
 */
export function previewTeamworkAward(): void {
  handleAchievementAwarded({
    type: MessageType.MessageTypeAchievementAwarded,
    achievement_id: 'teamwork_excellence',
    image_id: 'teamwork-excellence',
    name_key: 'achievements.teamworkExcellence.name',
    description_key: 'achievements.teamworkExcellence.description',
  });
}
